import React, { useCallback, useEffect, useState } from "react";
import { useWindowDimensions } from "react-native";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { signOut } from "../../services/userService";
import type RootStackParamList from "../../navigation/RootStackParamList";
import AppHeaderView from "./AppHeaderView";
import AppHeaderMenuModal from "./AppHeaderMenuModal";
import { setHeaderMenuHandlers } from "./headerMenuActions";
import { useAppSelector } from "../../redux/store";

type Props = {
  backgroundColor?: string;
  logoTextColor?: string;
  rightComponent?: React.ReactNode;
  onPressProfile?: () => void;
  onPressLogout?: () => void;
};

const MOBILE_BREAKPOINT = 768;

export default function AppHeaderContainer({
  backgroundColor,
  logoTextColor,
  rightComponent,
  onPressProfile,
  onPressLogout,
}: Props) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { width } = useWindowDimensions();
  const isMobile = width < MOBILE_BREAKPOINT;
  const isAdmin = useAppSelector((state) => state.user.admin);
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();

  useEffect(() => {
    setHeaderMenuHandlers({ onPressLogout });

    return () => {
      setHeaderMenuHandlers({ onPressLogout: null });
    };
  }, [onPressLogout]);

  const onPressMenu = useCallback(() => {
    if (isMobile) {
      navigation.navigate("HeaderMenu");
      return;
    }
    setIsMenuOpen(true);
  }, [isMobile, navigation]);

  const handleCloseMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const handleProfile = useCallback(() => {
    setIsMenuOpen(false);
    if (onPressProfile) {
      onPressProfile();
    } else {
      navigation.navigate("Profile");
    }
  }, [onPressProfile, navigation]);

  const handleAdminUsers = useCallback(() => {
    setIsMenuOpen(false);
    navigation.navigate("AdminUsers");
  }, [navigation]);

  const handleAdminConversations = useCallback(() => {
    setIsMenuOpen(false);
    navigation.navigate("AdminConversations");
  }, [navigation]);

  const handleAdminSignupFunnel = useCallback(() => {
    setIsMenuOpen(false);
    navigation.navigate("AdminSignupFunnel");
  }, [navigation]);

  const handleLogout = useCallback(async () => {
    setIsMenuOpen(false);
    if (onPressLogout) {
      onPressLogout();
      return;
    }
    const result = await signOut();
    if (!result.success) {
      console.error("Failed to sign out:", result.error);
    }
  }, [onPressLogout]);

  return (
    <>
      <AppHeaderView
        onPressMenu={onPressMenu}
        backgroundColor={backgroundColor}
        logoTextColor={logoTextColor}
        rightComponent={rightComponent}
      />
      <AppHeaderMenuModal
        visible={!isMobile && isMenuOpen}
        showAdminUsers={isAdmin}
        onClose={handleCloseMenu}
        onPressAdminUsers={handleAdminUsers}
        onPressAdminConversations={handleAdminConversations}
        onPressAdminSignupFunnel={handleAdminSignupFunnel}
        onPressProfile={handleProfile}
        onPressLogout={handleLogout}
      />
    </>
  );
}
